import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Paper,
    Typography,
    Button,
    CircularProgress,
    Alert,
    Card,
    CardContent,
    Grid,
    Divider,
    Chip
} from '@mui/material';
import {
    Download as DownloadIcon,
    Description as DescriptionIcon,
    School as SchoolIcon,
    Science as ScienceIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { activityService } from '../services/api';

interface SupportMaterial {
    bookNumber: number;
    activityNumber: number;
    title: string;
    description: string;
    hasDocument: boolean;
    icon: React.ReactNode;
}

const SupportMaterials: React.FC = () => {
    const navigate = useNavigate();
    const { language } = useLanguage();
    const [downloading, setDownloading] = useState<{ [key: string]: boolean }>({});
    const [error, setError] = useState<string | null>(null);

    const t = (es: string, en: string) => language === 'es' ? es : en;

    // Materiales de apoyo disponibles
    const supportMaterials: SupportMaterial[] = [
        {
            bookNumber: 1,
            activityNumber: 2,
            title: t('Actividad 2 - El Nuevo Traje del Rey', 'Activity 2 - The King\'s New Clothes'),
            description: t(
                'Documentación de apoyo para la actividad de experimentación con el traje del rey. Incluye instrucciones detalladas y materiales necesarios.',
                'Support documentation for the king\'s clothes experimentation activity. Includes detailed instructions and necessary materials.'
            ),
            hasDocument: true,
            icon: <SchoolIcon sx={{ fontSize: 40, color: '#1976d2' }} />
        },
        {
            bookNumber: 1,
            activityNumber: 4,
            title: t('Actividad 4 - La Liebre y la Tortuga', 'Activity 4 - The Hare and the Tortoise'),
            description: t(
                'Material de apoyo para la actividad de la carrera entre la liebre y la tortuga. Contiene guías de experimentación y análisis.',
                'Support material for the hare and tortoise race activity. Contains experimentation guides and analysis.'
            ),
            hasDocument: true,
            icon: <ScienceIcon sx={{ fontSize: 40, color: '#2e7d32' }} />
        }
    ];

    const handleDownload = async (bookNumber: number, activityNumber: number) => {
        const key = `${bookNumber}-${activityNumber}`;
        
        try {
            setDownloading(prev => ({ ...prev, [key]: true }));
            setError(null);
            
            await activityService.downloadDocumentation(bookNumber, activityNumber);
        } catch (err: any) {
            console.error('Error downloading documentation:', err);
            setError(err.response?.data?.detail || t('Error al descargar el material', 'Error downloading material'));
        } finally {
            setDownloading(prev => ({ ...prev, [key]: false }));
        }
    };

    const handleBackToDashboard = () => {
        navigate('/dashboard');
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
                <Box>
                    <Typography variant="h4" component="h1" fontWeight="bold" color="primary" gutterBottom>
                        {t('Material de Apoyo', 'Support Materials')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {t('Descarga la documentación adicional para las actividades que lo requieren', 'Download additional documentation for activities that require it')}
                    </Typography>
                </Box>
                <Button
                    variant="outlined"
                    onClick={handleBackToDashboard}
                    sx={{ minWidth: 120 }}
                >
                    {t('Volver al Dashboard', 'Back to Dashboard')}
                </Button>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Materiales disponibles */}
            <Grid container spacing={3}>
                {supportMaterials.map((material) => (
                    <Grid item xs={12} md={6} key={`${material.bookNumber}-${material.activityNumber}`}>
                        <Card 
                            elevation={3}
                            sx={{ 
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: 6
                                }
                            }}
                        >
                            <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                {/* Header del material */}
                                <Box display="flex" alignItems="center" mb={2}>
                                    {material.icon}
                                    <Box ml={2}>
                                        <Typography variant="h6" fontWeight="bold" color="primary">
                                            {material.title}
                                        </Typography>
                                        <Chip 
                                            label={`${t('Libro', 'Book')} ${material.bookNumber}`}
                                            size="small" 
                                            color="primary" 
                                            variant="outlined"
                                            sx={{ mt: 0.5 }}
                                        />
                                    </Box>
                                </Box>

                                <Divider sx={{ my: 2 }} />

                                {/* Descripción */}
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                                    {material.description}
                                </Typography>

                                {/* Botón de descarga */}
                                <Box display="flex" justifyContent="center">
                                    <Button
                                        variant="contained"
                                        startIcon={downloading[`${material.bookNumber}-${material.activityNumber}`] ? 
                                            <CircularProgress size={20} color="inherit" /> : 
                                            <DownloadIcon />
                                        }
                                        onClick={() => handleDownload(material.bookNumber, material.activityNumber)}
                                        disabled={downloading[`${material.bookNumber}-${material.activityNumber}`] || !material.hasDocument}
                                        sx={{
                                            minWidth: 200,
                                            py: 1.5,
                                            fontSize: '1rem',
                                            fontWeight: 'bold',
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            backgroundColor: '#1976d2',
                                            '&:hover': {
                                                backgroundColor: '#1565c0'
                                            }
                                        }}
                                    >
                                        {downloading[`${material.bookNumber}-${material.activityNumber}`] ? 
                                            t('Descargando...', 'Downloading...') : 
                                            t('Descargar Material', 'Download Material')
                                        }
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Información adicional */}
            <Paper elevation={1} sx={{ mt: 4, p: 3, bgcolor: '#f8f9fa' }}>
                <Box display="flex" alignItems="center" mb={2}>
                    <DescriptionIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" fontWeight="bold" color="primary">
                        {t('Información Importante', 'Important Information')}
                    </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t(
                        'Los materiales de apoyo están disponibles solo para las actividades que los requieren. Estos documentos contienen instrucciones detalladas, listas de materiales y guías paso a paso para realizar las actividades correctamente.',
                        'Support materials are available only for activities that require them. These documents contain detailed instructions, material lists, and step-by-step guides to perform the activities correctly.'
                    )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {t(
                        'Si tienes problemas para descargar algún material, asegúrate de tener una conexión estable a internet y contacta al soporte técnico si el problema persiste.',
                        'If you have trouble downloading any material, make sure you have a stable internet connection and contact technical support if the problem persists.'
                    )}
                </Typography>
            </Paper>
        </Container>
    );
};

export default SupportMaterials;
